import React from 'react'
import {
  AdCreative,
  AdCreativeStatus,
  PublishCampaignDTO,
  resolveOptimizationDefinition,
} from '@marketing-milk/interfaces'
import { equals } from 'lodash/fp'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { hasImproperCTA, isValidCreative } from '../../../campaigns/helpers/campaign.helpers'
import { AdCreativeList } from './'

// Facebook rejects the 51st ad in an ad set (error #1487809), and paused ads still count
// toward it. Because every selected creative is attached to every selected ad set, this
// selection count IS the ad count per ad set — so Meta's limit is our limit exactly.
// The previous cap of 26 was the alphabet (see creativeLetter), not a platform rule.
const MAX_SELECTED_CREATIVES = 50

type Props = {
  creatives?: AdCreative[]
  selectedCreatives: AdCreative[]
  campaignDto: PublishCampaignDTO
  onChangeCreatives: (creatives: AdCreative[]) => void
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  isFetching?: boolean
  skipCreativeValidation?: boolean
}

export function AdCreativeStep({
  campaignDto,
  selectedCreatives,
  onChangeCreatives,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
  skipCreativeValidation,
  ...props
}: Props) {
  const [creatives, setCreatives] = useState(props.creatives)

  useEffect(() => {
    if (!props.creatives) return
    setCreatives(filterCreatives(props.creatives))
  }, [props.creatives])

  const filterCreatives = (creatives: AdCreative[]) => {
    if (skipCreativeValidation) return creatives
    const optimization = resolveOptimizationDefinition(campaignDto.campaign)
    return creatives
      .filter(creative => creative.status !== AdCreativeStatus.DELETED) // not deleted
      .filter(creative => creative.status !== AdCreativeStatus.WITH_ISSUES) // has no issues
      .filter(creative => !hasImproperCTA(creative, optimization)) // doesn't have config issues with campaign optimization
      .filter(creative => isValidCreative(creative))
  }

  const selectCreative = (newCreative: AdCreative) => {
    const alreadySelected = selectedCreatives.some(c => equals(c, newCreative))
    if (alreadySelected) {
      onChangeCreatives(selectedCreatives.filter(c => !equals(c, newCreative)))
    } else if (selectedCreatives.length < MAX_SELECTED_CREATIVES) {
      onChangeCreatives([...selectedCreatives, newCreative])
    } else {
      // Previously this branch was missing entirely, so taps past the cap silently no-opped.
      toast.warn(
        `Facebook allows ${MAX_SELECTED_CREATIVES} ads per ad set, so you can't select more than ${MAX_SELECTED_CREATIVES} creatives. Deselect one to swap it out.`
      )
    }
  }

  const selectAll = (visibleCreatives: AdCreative[]) => {
    const selectedIds = new Set(selectedCreatives.map(c => c.id))
    const additions = visibleCreatives.filter(c => !selectedIds.has(c.id))
    const remainingSlots = MAX_SELECTED_CREATIVES - selectedCreatives.length
    if (remainingSlots <= 0 || additions.length === 0) return

    const toAdd = additions.slice(0, remainingSlots)
    onChangeCreatives([...selectedCreatives, ...toAdd])

    if (additions.length > toAdd.length) {
      toast.warn(
        `Facebook allows ${MAX_SELECTED_CREATIVES} ads per ad set, so only the first ${toAdd.length} were selected.`
      )
    }
  }

  const deselectAll = (visibleCreatives: AdCreative[]) => {
    const visibleIds = new Set(visibleCreatives.map(c => c.id))
    onChangeCreatives(selectedCreatives.filter(c => !visibleIds.has(c.id)))
  }

  return (
    <AdCreativeList
      creatives={creatives}
      selectedCreatives={selectedCreatives}
      selectCreative={selectCreative}
      onSelectAll={selectAll}
      onDeselectAll={deselectAll}
      maxSelectable={MAX_SELECTED_CREATIVES}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isFetching={isFetching}
    />
  )
}
